<template>
  <div class="stats-bar" :class="{ 'stats-bar--loading': loading }">
    <div v-if="showPeriodSelector" class="stats-bar__header">
      <select
        v-model="selectedPeriod"
        class="stats-bar__period-select"
        @change="handlePeriodChange"
      >
        <option v-for="option in periodOptions" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
      <VueDatePicker
        v-if="selectedPeriod === 'custom'"
        v-model="dateRange"
        range
        multi-calendars
        auto-apply
        :max-date="new Date()"
        :formats="dateFormats"
        :teleport="true"
        class="stats-bar__datepicker"
        @update:model-value="handleDateRangeChange"
      />
    </div>
    <div class="stats-bar__cards">
      <slot></slot>
    </div>
  </div>
</template>

<script>
  /**
   * StatsBar Component
   *
   * Container component for StatsCard components with optional period selector.
   * Provides consistent layout and period filtering for dashboard statistics.
   *
   * @example
   * <StatsBar v-model:period="selectedPeriod" @period-change="loadStats">
   *   <StatsCard label="Total" :value="100" />
   *   <StatsCard label="Pending" :value="25" />
   * </StatsBar>
   */
  import { defineComponent, ref, computed, watch, getCurrentInstance } from 'vue';
  import { VueDatePicker } from '@vuepic/vue-datepicker';
  import '@vuepic/vue-datepicker/dist/main.css';

  export default defineComponent({
    name: 'StatsBar',

    components: {
      VueDatePicker,
    },

    props: {
      /**
       * Currently selected period
       */
      period: {
        type: String,
        default: '30d',
      },
      /**
       * Whether to show the period selector
       */
      showPeriodSelector: {
        type: Boolean,
        default: true,
      },
      /**
       * Loading state
       */
      loading: {
        type: Boolean,
        default: false,
      },
      /**
       * Custom period options (overrides defaults)
       */
      customPeriodOptions: {
        type: Array,
        default: null,
      },
    },

    emits: ['update:period', 'period-change', 'date-range-change'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance?.appContext?.config?.globalProperties?.$t || ((key) => key);

      const selectedPeriod = ref(props.period);

      const now = new Date();
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const dateRange = ref([thirtyDaysAgo, now]);

      // Default period options with translations
      const defaultPeriodOptions = computed(() => [
        { value: '30d', label: $t('stats.periods.last_30_days') },
        { value: '90d', label: $t('stats.periods.last_quarter') },
        { value: 'ytd', label: $t('stats.periods.year_to_date') },
        { value: 'all', label: $t('stats.periods.all_time') },
        { value: 'custom', label: $t('stats.periods.custom_range') },
      ]);

      const periodOptions = computed(() => {
        return props.customPeriodOptions || defaultPeriodOptions.value;
      });

      // Sync with v-model
      watch(
        () => props.period,
        (newVal) => {
          selectedPeriod.value = newVal;
        }
      );

      const handlePeriodChange = () => {
        emit('update:period', selectedPeriod.value);
        emit('period-change', selectedPeriod.value);
        if (selectedPeriod.value === 'custom' && dateRange.value?.[0] && dateRange.value?.[1]) {
          emit('date-range-change', { start: dateRange.value[0], end: dateRange.value[1] });
        }
      };

      const handleDateRangeChange = (val) => {
        if (val && val[0] && val[1]) {
          emit('date-range-change', { start: val[0], end: val[1] });
        }
      };

      const pad = (n) => String(n).padStart(2, '0');
      const fmtDate = (d) => {
        const dt = new Date(d);
        return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()}`;
      };

      const dateFormats = {
        input: (dates) => {
          if (!dates || !Array.isArray(dates)) return '';
          if (dates[0] && dates[1]) return `${fmtDate(dates[0])} – ${fmtDate(dates[1])}`;
          if (dates[0]) return fmtDate(dates[0]);
          return '';
        },
      };

      return {
        selectedPeriod,
        periodOptions,
        dateRange,
        dateFormats,
        handlePeriodChange,
        handleDateRangeChange,
      };
    },
  });
</script>

<style scoped lang="scss">
  .stats-bar {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 24px;

    &__header {
      display: flex;
      align-items: center;
    }

    &__cards {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
    }

    &__datepicker {
      margin-left: 8px;
      max-width: 240px;

      :deep(.dp__input) {
        font-weight: 500;
        min-height: unset;
        height: auto;
        padding: 0.5rem 0.75rem 0.5rem 2rem;
        width: 100%;
        border-radius: 0.5rem;
        font-size: 0.875rem;
        line-height: 1.25rem;
        font-family: inherit;
        box-shadow:
          0 0 rgba(0, 0, 0, 0),
          0 0 rgba(0, 0, 0, 0),
          0 1px 2px 0 rgba(0, 0, 0, 0.05);
        border: 1px solid var(--slate-200);
        background-color: var(--white);
      }
    }

    &__period-select {
      padding: 4px 24px 4px 8px;
      font-size: 12px;
      font-weight: 500;
      color: var(--slate-500);
      background-color: transparent;
      border: none;
      cursor: pointer;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 4px center;
      background-size: 12px;
      transition: color 0.15s ease;

      &:hover {
        color: var(--slate-700);
      }

      &:focus {
        outline: none;
        color: var(--slate-700);
      }
    }

    // Responsive
    @media (max-width: 768px) {
      &__cards {
        flex-direction: column;
      }
    }
  }
</style>
